import { SDKResponse } from '@commercetools/frontend-sdk';
import { CloudinaryFinishAssetGroup } from 'shared/types/content';
import useSWR from 'swr';
import { dedupeCloudinaryImages } from 'helpers/mappers/cloudinaryAssetToImage';
import { sdk } from 'sdk';

const NO_IMAGES = { skuImages: [], speciesImages: [], genericImages: [] };

/**
 * Gallery images for the selected finish, split by specificity so the caller can rank them:
 *
 * - `skuImages` — tagged to this SKU + finish; these replace the base gallery.
 * - `speciesImages` — tagged `finish_species:<speciesSlug>:<finishKey>`, shared across every
 *   SKU of one species in that finish (wood stains).
 * - `genericImages` — tagged `finish:<finishKey>`, shared across every product with that
 *   finish (powder coat).
 *
 * Both generic tiers are appended after the product's own images, more specific tier first.
 * `species` must already be tag-slugged (see `toTagSlug`); omit it for products without one.
 */
export const useProductFinishImages = (productId: string, finish?: string, species?: string) => {
  const response = useSWR<SDKResponse<CloudinaryFinishAssetGroup>>(
    finish ? ['action/assets/getProductFinishAssets', productId, finish, species ?? ''] : null,
    () =>
      sdk.callAction<CloudinaryFinishAssetGroup>({
        actionName: 'assets/getProductFinishAssets',
        query: { productId, finish: finish ?? '', ...(species ? { species } : {}) },
      }),
    // Hold the previous finish's images on screen while the new finish loads, matching
    // useProductBaseImages. Without it the gallery empties for a render on every cache miss,
    // which both flashes the base images and rebuilds the gallery against an image set that
    // does not match the selected finish.
    { keepPreviousData: true },
  );

  if (!response.data || response.data.isError) return NO_IMAGES;

  const { primary, additional, speciesGeneric, generic } = response.data.data;

  return {
    skuImages: dedupeCloudinaryImages([...primary.slice(0, 1), ...additional]),
    speciesImages: dedupeCloudinaryImages(speciesGeneric ?? []),
    genericImages: dedupeCloudinaryImages(generic ?? []),
  };
};
